import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { useGoogleAccessToken } from "@/hooks/cloud/useGoogleAccessToken";
import { CloudFile } from "@/types/cloud";

const GOOGLE_API_SCRIPT_ID = "google-api-script";
const GOOGLE_GSI_SCRIPT_ID = "google-gsi-script";
const GOOGLE_API_SRC = "https://apis.google.com/js/api.js";
const GOOGLE_GSI_SRC = "https://accounts.google.com/gsi/client";
const GOOGLE_PICKER_API = "picker";
const GOOGLE_PICKER_VIEW_ID = "DOCS";
const GOOGLE_PICKER_MODE_LIST = "LIST";
const GOOGLE_PICKER_SCOPE = "https://www.googleapis.com/auth/drive.file";

const loadScript = (id: string, src: string) =>
  new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(id) as HTMLScriptElement | null;
    if (existingScript) {
      if (existingScript.dataset.loaded === "true") {
        resolve();
        return;
      }

      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener("error", () => reject(new Error(`Failed to load ${src}`)), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(script);
  });

const ensurePickerApiLoaded = async () => {
  await Promise.all([
    loadScript(GOOGLE_API_SCRIPT_ID, GOOGLE_API_SRC),
    loadScript(GOOGLE_GSI_SCRIPT_ID, GOOGLE_GSI_SRC),
  ]);

  if (!window.gapi) {
    throw new Error("Google API client failed to initialize");
  }

  await new Promise<void>((resolve, reject) => {
    try {
      window.gapi?.load(GOOGLE_PICKER_API, () => resolve());
    } catch (error) {
      reject(error);
    }
  });

  if (!window.google?.picker) {
    throw new Error("Google Picker API failed to initialize");
  }
};

const mapPickedFiles = (documents: Record<string, unknown>[]): CloudFile[] => {
  const picker = window.google?.picker;
  if (!picker) {
    return [];
  }

  return documents
    .map((document) => ({
      id: String(document[picker.Document.ID] ?? ""),
      name: String(document[picker.Document.NAME] ?? ""),
      mimeType: String(document[picker.Document.MIME_TYPE] ?? "application/octet-stream"),
      size: Number(document[picker.Document.SIZE_BYTES] ?? 0) || undefined,
      modifiedTime: document[picker.Document.LAST_EDITED_UTC]
        ? new Date(Number(document[picker.Document.LAST_EDITED_UTC])).toISOString()
        : undefined,
    }))
    .filter((file) => file.id && file.name);
};

interface OpenGoogleDrivePickerOptions {
  fileIds?: string[];
  onFilesPicked: (files: CloudFile[]) => void;
}

export const useGoogleDrivePicker = () => {
  const { token, isLoading: isTokenLoading, error: tokenError, refetch } = useGoogleAccessToken();
  const [isOpening, setIsOpening] = useState(false);
  const pickerReadyRef = useRef(false);

  const openPicker = useCallback(
    async ({ fileIds = [], onFilesPicked }: OpenGoogleDrivePickerOptions) => {
      const developerKey = import.meta.env.VITE_GOOGLE_API_KEY;
      const appId = import.meta.env.VITE_GOOGLE_APP_ID;

      if (!developerKey) {
        throw new Error("VITE_GOOGLE_API_KEY is not configured");
      }

      if (!appId) {
        throw new Error("VITE_GOOGLE_APP_ID is not configured");
      }

      setIsOpening(true);

      try {
        if (!pickerReadyRef.current) {
          await ensurePickerApiLoaded();
          pickerReadyRef.current = true;
        }

        const accessToken = token || (await refetch()).data;
        if (!accessToken) {
          throw new Error("Google Drive access token is unavailable");
        }

        const picker = window.google?.picker;
        if (!picker) {
          throw new Error("Google Picker API is unavailable");
        }

        const docsView = new picker.DocsView(
          picker.ViewId[GOOGLE_PICKER_VIEW_ID as keyof typeof picker.ViewId],
        );
        docsView.setIncludeFolders(true);
        docsView.setSelectFolderEnabled(false);
        docsView.setMode(GOOGLE_PICKER_MODE_LIST);

        if (fileIds.length > 0) {
          docsView.setFileIds(fileIds.join(","));
        }

        const pickerInstance = new picker.PickerBuilder()
          .addView(docsView)
          .enableFeature(picker.Feature.MULTISELECT_ENABLED)
          .enableFeature(picker.Feature.SUPPORT_DRIVES)
          .setAppId(appId)
          .setDeveloperKey(developerKey)
          .setOAuthToken(accessToken)
          .setCallback((data) => {
            const action = data[picker.Response.ACTION] as string | undefined;

            if (action === picker.Action.PICKED) {
              const selectedFiles = mapPickedFiles(
                (data[picker.Response.DOCUMENTS] as Record<string, unknown>[]) ?? [],
              );
              onFilesPicked(selectedFiles);
            }
          })
          .build();

        pickerInstance.setVisible(true);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to open Google Drive picker";
        console.error("[Google Picker] Failed to open picker", error);
        toast.error(message);
        throw error;
      } finally {
        setIsOpening(false);
      }
    },
    [refetch, token],
  );

  return {
    openPicker,
    isLoading: isOpening || isTokenLoading,
    error: tokenError,
    requiredScope: GOOGLE_PICKER_SCOPE,
  };
};
